import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import CloseIcon from '../icons/CloseIcon';
import { RefreshIcon } from '../icons/RefreshIcon';
import OpenNewTabIcon from '../icons/OpenNewTabIcon';

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    backdrop-filter: blur(5px);

    @media (max-width: 768px) {
        align-items: flex-start;
        padding-top: 41px;
    }
`;

const ModalContainer = styled.div`
    background: rgba(28, 28, 28, 0.9);
    border-radius: 8px;
    width: 80%;
    max-width: 800px;
    overflow: hidden;
    padding: 20px;
    position: relative;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    max-height: 90vh;

    @media (max-width: 768px) {
        width: 100vw;
        height: calc(100vh - 41px);
        max-height: calc(100vh - 41px);
        border-radius: 0;
    }
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding-bottom: 10px;
    flex-shrink: 0;
`;

const ModalTitle = styled.h3`
    color: #fff;
    font-size: 18px;
    margin: 0;
    font-weight: 500;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
`;

const ModalActions = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
`;

const ActionButton = styled.button`
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: #fff;
    padding: 8px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;
    width: 32px;
    height: 32px;

    &:hover {
        background: rgba(255, 255, 255, 0.2);
    }

    svg {
      width: 16px;
      height: 16px;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    color: #B6C2CF;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;
    padding: 8px;

    &:hover {
        color: #fff;
    }
`;

const ContentArea = styled.div`
    color: #B6C2CF;
    font-size: 14px;
    line-height: 1.6;
    overflow-y: auto;
    padding: 10px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    word-wrap: break-word;
    overflow-x: hidden;
    flex-grow: 1;

    &::-webkit-scrollbar {
        width: 8px;
    }
    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 6px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 6px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.5);
    }
`;

const LoadingSpinner = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: #B6C2CF;
`;

const NoContentMessage = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: #B6C2CF;
    font-style: italic;
`;

const SavePreviewModal = ({ isOpen, onClose, save, onRefresh, onOpenFullPage, contentLoading = {} }) => {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [currentSave, setCurrentSave] = useState(save);

    useEffect(() => {
        if (isOpen) {
            setCurrentSave(save);
        }
    }, [isOpen, save]);

    if (!isOpen) return null;

    const handleRefresh = async () => {
        setIsRefreshing(true);
        const updatedSave = await onRefresh(save.id, true);
        if (updatedSave) {
            setCurrentSave(updatedSave);
        }
        setIsRefreshing(false);
    };

    const isLoading = contentLoading[save?.id] || isRefreshing;

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <ModalTitle>{currentSave?.title}</ModalTitle>
                    <ModalActions>
                        <ActionButton onClick={handleRefresh} disabled={isLoading} title="Refresh">
                            <RefreshIcon />
                        </ActionButton>
                        <ActionButton onClick={() => onOpenFullPage(currentSave.url)} title="Open in new tab">
                            <OpenNewTabIcon />
                        </ActionButton>
                        <CloseButton onClick={onClose} title="Close">
                            <CloseIcon />
                        </CloseButton>
                    </ModalActions>
                </ModalHeader>
                <ContentArea>
                    {isLoading ? (
                        <LoadingSpinner>Loading content...</LoadingSpinner>
                    ) : currentSave.content ? (
                        <div dangerouslySetInnerHTML={{ __html: currentSave.content }} />
                    ) : (
                        <NoContentMessage>No content available. Try refreshing.</NoContentMessage>
                    )}
                </ContentArea>
            </ModalContainer>
        </ModalOverlay>
    );
};

export default SavePreviewModal;
